import featureFlagEnabled from './featureFlagEnabled';
/**
 * This mixin is specifically created to house & share
 * re-usable bits across all components in `components/reservation`.
 * https://vuejs.org/v2/guide/mixins.html
 *
 * Unfortunately this mixin also includes methods related to
 * the management of "modals", which should be separated into
 * their own mixin, using their own "module" within the Vuex "store".
 */
export const reservation = {
  mixins: [featureFlagEnabled],
  data() {
    return {
      /** @type {object} map of fulfillment type display names */
      fulfillmentTypeMap: {
        pickup: "curbside",
        curbside: "curbside",
        delivery: "delivery"
      }
    };
  },

  computed: {
    /** @return {object} cart from store */
    cart() {
      return this.$store.state.cart;
    },

    /** @return {object} reservation from store (or passed in as prop, for style guide) */
    reservation() {
      return this.$store.state.reservation;
    },

    /** @return {string} fulfillment type */
    fulfillmentType() {
      return this.reservation.fulfillmentType || "";
    },

    /** @return {boolean} is the current reservation delivery? */
    isDelivery() {
      return this.fulfillmentType === "delivery";
    },

    isFavorDeliveryEligible() {
      return this.$store.state.timeslots.favorDeliveryEligible;
    },

    /** @return {string} my timeslot's id, from props */
    timeslotId() {
      return this.reservation.timeslotId;
    },

    /** @return {object} reservation expiration */
    expiration() {
      return this.reservation.expiration || {};
    },

    /** @return {boolean} is the timeslot still active (not expired)? */
    isActiveReservation() {
      return this.expiration.isActive;
    },

    /** @return {object} context options */
    contextOptions() {
      return this.reservation.context.options || {};
    },

    /**
     * @return {boolean} should we show the modal with this id?
     * REQUIRED: must set data property `modalId` within component
     */
    shouldShowModal: {
      get() {
        const { context } = this.$store.state.reservation;

        // React handles the change timeslot modal
        if (context && context.modalId === 'change-timeslot-modal') {
          return false;
        }

        return context && context.modalId === this.modalId;
      },
      set(isShown) {
        const vm = this;

        // fire optional handler method
        vm.shouldShowModalHandler(vm);
      }
    }
  },

  methods: {
    /**
     * show a modal (also hides any other open modals)
     * @param {string} modalId - id of the modal we want to open
     * @param {object} [options] - any options we want to set for the modal
     */
    showModal(modalId, options = {}) {
      this.$store.commit("reservation/SET_CONTEXT", {
        modalId,
        options
      });
    },

    /**
     * helper function to show a modal (also hides any other open modals)
     * @param {string} modalId - id of the modal we want to open
     * @param {object} [options] - any options we want to set for the modal
     */
    openModal(modalId, options = {}) {
      this.showModal(modalId, options);
    },

    /** helper function to clear context */
    closeModal() {
      this.clearContext();
    },

    /** helper function to clear context */
    cancelModal() {
      this.clearContext();
    },

    /** helper function to clear context */
    hideModal() {
      this.clearContext();
    },

    /**
     * clear the context (in effect this hides all modals)
     * @param {string} modalId - id of the modal we want to open
     */
    clearContext() {
      this.$store.commit("reservation/SET_CONTEXT", {});
    },

    /**
     * click handler for main modal buttons (cancel, ok, x)
     * @param {string} type - property of context.options to run as a function
     */
    modalClickHandler(type) {
      // cache this, for use below
      const vm = this;

      // cache context options now, so we can clear them next
      const { contextOptions } = this;

      // clear context whenever the user clicks "cancel" or "close"
      if (type === "cancel" || type === "close") {
        this.clearContext();
      }

      // run this type fn
      if (typeof contextOptions[type] === "function") {
        contextOptions[type](vm);
      }
    },

    /** fn to handle the modal's "hidden" event */
    hiddenEventHandler() {
      // Check if the recently hidden modal is still set as the context
      // (this happens when the user hit ESC or clicked on the background, to close)
      if (this.$store.state.reservation.context.modalId === this.modalId) {
        this.clearContext();
      }
    },

    /** if not already on the cart page, forward there */
    showTheCart() {
      const notOnTheCartPage = !!window.location.pathname.indexOf("/cart");

      if (notOnTheCartPage) {
        window.location = "/cart";
      } else {
        this.hideModal();
      }
    },

    /** callback fn to fire whenever "shouldShow" v-model is set to true */
    shouldShowModalHandler(vm) {
      // this is a place holder to help declare the api, and to prevent the need for error handling
      // add this to your `methods` in your component to override this empty function
      // https://vuejs.org/v2/guide/mixins.html#Option-Merging
    }
  }
};
